import React, { useState, useEffect, useRef } from 'react';

interface SharedRunInputDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (inputMessage: string) => void;
  workflowName: string;
  stepLabel?: string;
  stepDescription?: string;
  stepInputMessage?: string;
}

const SharedRunInputDialog: React.FC<SharedRunInputDialogProps> = ({
  isOpen,
  onClose,
  onSubmit,
  workflowName,
  stepLabel,
  stepDescription,
  stepInputMessage,
}) => {
  const [inputMessage, setInputMessage] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const dialogTitleId = 'shared-run-input-dialog-title';

  useEffect(() => {
    if (isOpen) {
      setInputMessage('');
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const value = inputMessage.trim();
    if (value) {
      onSubmit(value);
    }
  };

  if (!isOpen) return null;

  const label = stepLabel || 'Input Message';
  const placeholder = stepInputMessage || stepDescription || 'Enter your input message...';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        role="dialog"
        aria-labelledby={dialogTitleId}
        aria-modal="true"
        className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 w-full max-w-md mx-4"
      >
        <h2
          id={dialogTitleId}
          className="text-lg font-semibold text-gray-900 dark:text-white mb-4"
        >
          Run {workflowName}
        </h2>

        <form onSubmit={handleFormSubmit}>
          <div className="mb-4">
            <label
              htmlFor="shared-run-input-message"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >
              {label}
            </label>
            {stepDescription && stepLabel && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                {stepDescription}
              </p>
            )}
            <input
              ref={inputRef}
              id="shared-run-input-message"
              type="text"
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              placeholder={placeholder}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!inputMessage.trim()}
              className="px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Run
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SharedRunInputDialog;
